export interface VirtualPrinterConfig {
  hasMulti: boolean;
  hasPublipostage: boolean;
  physique: CourrierPhysiqueConfig;
  electronique: CourrierElectroniqueConfig;
  hasPhysique: boolean;
  hasElectronique: boolean;
  hasEmail: boolean;
  hasSMS: boolean;
  hasDigiposte: boolean;
  showPrice?: boolean;

  sender_info: SenderInfo;
  sender_info_careOf: SenderInfo;

  indexes?: IndexConfig[];
}

export const SENDING_TYPES = {
  PHYSIQUE: 'physique',
  ELECTRONIQUE: 'electronique',
} as const;

// Available mask images in assets/images/virtual-printer-new/
export const MASKS_BASE_PATH = 'assets/images/virtual-printer-new';
export const AVAILABLE_MASKS = ['platform3', 'platformDE'] as const;
export const DEFAULT_MASK: MaskName = 'platform3';

export type MaskName = (typeof AVAILABLE_MASKS)[number];

export const CANALS = {
  MOD: 'MOD',
  LRE: 'LRE',
} as const;

export interface CourrierPhysiqueConfig {
  hasEcopli: boolean;
  hasLettreVerte: boolean;
  hasInternational: boolean;
  hasReco: boolean;
  hasRecoAR: boolean;
  hasRecoLrel: boolean;
  hasRecoLrelAR: boolean;
  hasOptionSuivi: boolean;
  hasDestineo: boolean;
  hasStandard: boolean;
  hasKompakt: boolean;
  hasGross: boolean;
  hasColor: boolean;
  hasB_and_W: boolean;
  hasDoubleSidedPrinting: boolean;
  hasSingleSidedPrinting: boolean;
  hasCareOf: boolean;
  careOfLabel: string;
  /** @deprecated */
  zoneCapture?: ZoneCapture;
  zoneCapturePlatform: Array<Record<string, ZoneCapture>>;
  senderTypePlatform: Array<Record<string, { countryCode: string; country: string }>>;
  cuttingFoldZone?: ZoneCapture;
  default?: {
    sendingType: string;
    color: boolean;
    impressionType: ImpressionType;
  };
  ref_return_envelop: Array<Record<string, string>>;
  mask: Array<Record<string, string>>;
  senderType?: Record<string, SenderMode>;
  postalClassSenderDisplay?: Array<Record<string, 'yes' | 'no'>>;
}

export type SenderMode = 'Provider' | 'Client';

export interface CourrierElectroniqueConfig {
  hasLRE: boolean;
  hasERE: boolean;
  hasAvisElectronique: boolean;
  default?: {
    sendingType: string;
  };
}

export interface ZoneCapture {
  width: number;
  startY: number;
  startX: number;
  height: number;
}

export enum ImpressionType {
  RECTO = 'recto',
  RECTO_VERSO = 'recto_verso',
}

export interface SenderInfo {
  department: string;
  office_location: string;
  sender: Sender;
  sender_provider?: Sender;
  listPostalReturnAddress?: PostalReturnAddressEntry[];
}
export interface PostalReturnAddressEntry {
  Return_Address: ReturnAddress;
}

export interface ReturnAddress {
  Return_code: string;
  Return_country: string;
  Return_postal_address_Line_1: string;
  Return_postal_address_Line_2: string;
  Return_postal_address_Line_3: string;
  Return_postal_address_Line_4: string;
  Return_postal_address_Line_5: string;
  Return_postal_address_Line_6: string;
  Return_postal_address_Zip_Code: string;
  Return_postal_address_Town: string;
  Return_postal_address_CIP_1: string;
  Return_postal_address_CIP_2: string;
  id?: string;
}

export interface Sender {
  value?: string;
  address?: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  addressLine5: string;
  addressLine6: string;
  country: string;
  countryCode: string;
  cip1?: string;
  cip2?: string;
}

export interface SendingOption {
  id: string;
  value: string;
  label: string;
  condition?: string;
  maxPages?: number;
  hasTooltip?: boolean;
  tooltipKey?: string;
  extraInfo?: boolean;
  senderTypeKey?: string;
}

export interface Fold {
  name: string;
  address?: Address;
  startCourrielIndice: number;
  endCourrielIndice: number;
  mainLetterPages: number;
  sheets: number;
  pages: number;

  //PHYSIQUE
  porteAdressePages?: number;
  addFlyLeaf?: boolean;

  //ELECTRONIQUE
  recipient: FoldRecipient;
}

export interface Address {
  address: string;
  conform: boolean;
  address_edited_by_user: boolean;
  country: string;
  errorReason?: string;
}

export interface FoldRecipient {
  dest_status?: 'particulier' | 'professionnel';
  identifier?: string;
  firstName?: string;
  lastName?: string;
  to_company?: string;
}

export interface IndexValue {
  code: string;
  label: string;
  zip_used_index?: boolean;
  RA?: { id: string } | null;
}

export interface IndexConfig {
  index_name: string;
  index_name_front: string;
  index_label_sentence: string;
  index_global: boolean;
  mandatory: boolean;
  regex?: string;
  values: string[] | IndexValue[] | string;
  affected_by?: {
    [parentIndexName: string]: {
      [parentValue: string]: Partial<IndexConfig>;
    };
  };
  zoneCapture?: ZoneCapture;
}

//This URL is generated by C# app pipeline, DO NOT CHANGE IT without updated C# app pipeline
export const PrinterAppUrl = './MyPBConnect/';

export type PrinterAppVersion = {
  version: string;
  targetFramework: string;
  date: Date;
};

export interface ApplicationData {
  canal: string; // MOD OU LRE
  type_envoi: string;
  impression?: ImpressionType;
  couleur?: string;
  ref_return_envelop?: string;
}

export interface FileData {
  name: string;
  pages: number;
  file: File;
  documentType: string;
}

export interface Recipient {
  country?: string;
  countryCode?: string;
  zipCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  addressLine5?: string;
  addressLine6?: string;
  pagePorteAdresse?: 'oui' | 'non';
  split_nb_pages?: string;

  dest_status?: 'particulier' | 'professionnel';
  identifier?: string;
  firstName?: string;
  lastName?: string;
  to_company?: string;

  index?: { [key: string]: string };
}

export interface Document {
  nom: string;
  type: string;
  num_page: string;
  derniere_page: '0' | '1';
  part: string;
  split_nb_pages?: 'variable';
  index?: number;
}

export type BackgroundPdfType = 'first' | 'all' | 'last';
